import { Dialog, DialogTitle } from '@mui/material'
import { useOpenFeatureClient } from '@openfeature/react-sdk'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierType, CarrierVisitDirection, CompanyResponseDto } from '@planning/app/api'
import { BaplieParseResult } from '@planning/pages/Order/stores/OrderListUploadParsingStore'
import { useTranslate } from '@tolgee/react'
import { CloseIcon, IconButton } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { OrderListUploadDialogBody } from './OrderListUploadDialogBody'
import { OrderListUploadDialogFooter } from './OrderListUploadDialogFooter'
import { OrderListUploadStepper } from './OrderListUploadStepper'
import { useOrderListUploadError } from './hooks/useOrderListUploadError'

interface IProps {
  open: boolean
  enableFieldSelectionFromOrdersView: boolean
  handleDialogClose: () => Promise<void> | void
  visitType?: CarrierType
  isRestow?: boolean
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
}

export interface IOrderListUploadFormData {
  customer?: CompanyResponseDto
  direction: CarrierVisitDirection
}

export const OrderListUploadDialogV2: FC<IProps> = observer(
  ({
    open,
    visitType,
    enableFieldSelectionFromOrdersView,
    handleDialogClose,
    isRestow,
    parseBaplie,
  }) => {
    const {
      appViewStore,
      orderListUploadViewStoreV2: store,
      tenantStore,
      restowItemStore,
    } = usePlanningStore()
    const { handleError } = useOrderListUploadError(store)
    const { t } = useTranslate()
    const featureFlagClient = useOpenFeatureClient()

    const {
      handleSubmit,
      register,
      setValue,
      control,
      reset,
      formState: { errors },
    } = useForm({
      defaultValues: {
        customer: undefined,
        direction: CarrierVisitDirection.Inbound,
      } as IOrderListUploadFormData,
    })

    useEffect(() => {
      if (parseBaplie) {
        const isEnabled = featureFlagClient.getBooleanValue('baplie-parser-replacement', false)

        store.connectBaplieParser(parseBaplie, isEnabled)
      }
    }, [store, parseBaplie, featureFlagClient])

    const isForRestow = useMemo(() => store.isRestow ?? isRestow, [isRestow, store.isRestow])

    const handleValidateAndSubmit = async (data: IOrderListUploadFormData) => {
      if (!tenantStore.tenant) return

      store.parsingStore.setUploadFailedReason()

      const { parsingStore } = store
      store.setCustomer(data.customer)
      if (tenantStore.tenant.unloCodes.length)
        parsingStore.setUNLOCodes(tenantStore.tenant.unloCodes.map(i => i.name!))
      // [UNLOCodes] TODO: Review
      else parsingStore.setUNLOCodes(['DEHAM'])

      await parseOrderList()

      if (store.parsingStore.uploadFailedReason) return

      if (store.isVesselVisitMismatch) {
        store.setShowVesselVisitMismatchWarning(true)
        return
      }

      store.setActiveStep(1)
    }

    const parseOrderList = async () => {
      try {
        await store.parseOrderList(isForRestow)

        if (!store.parsingStore.file) {
          store.setUploadFailedReason(
            t(
              'noFileAddedForUploadPleaseChooseAFile',
              'No file added for upload, please choose a file',
            ),
          )
          return
        }

        if (!isForRestow && !store.isGeneralCargoVisit) {
          const restows = restowItemStore.unreadRestows.filter(
            restow => restow.vesselVisitId === store.vesselVisitId,
          )

          const unmatchedRestows = restows.filter(
            restow =>
              !store.dataStore.remainOnBoard.some(order => order.containerNumber === restow.number),
          )

          if (unmatchedRestows.length > 0) {
            const confirmed = await appViewStore.setOpenConfirmDialog(
              true,
              t(
                'followingContainerFromRestowListWereNotFound',
                `The following containers from the restow list were not found in the uploaded file: ${unmatchedRestows.map(restow => restow.number).join(', ')}. Do you want to proceed?`,
              ),
            )

            if (!confirmed) {
              return
            }
          }
        }

        if (
          !store.isGeneralCargoVisit &&
          !store.dataStore.ordersFromUpdate.length &&
          !isForRestow
        ) {
          const { unloCodes } = store.parsingStore
          const operation =
            store.direction === CarrierVisitDirection.Inbound ? 'discharge' : 'loading'

          const noContainersFoundMessage = `${t(
            'noContainersFoundFor',
            'No containers found for',
          )} ${operation} ${t('atTheConfiguredPortCodes', 'at the configured port codes')} (${unloCodes.join(', ')}).`

          store.setUploadFailedReason(noContainersFoundMessage)
          return
        }

        if (store.isGeneralCargoVisit && !store.generalCargoUploadOrders.length && !isForRestow) {
          store.setUploadFailedReason(t('noRecordsFoundInCsv', 'No records found in csv file'))
          return
        }
      } catch (error: any) {
        handleError(error)
      }
    }

    return (
      <Dialog
        open={open}
        sx={{ zIndex: '1000' }}
        fullWidth={true}
        maxWidth='md'
        slotProps={{
          paper: {
            sx: {
              minHeight: '90vh',
              maxHeight: 'none',
            },
          },
        }}
      >
        <DialogTitle>
          {isForRestow ? t('uploadRestows', 'Upload restows') : t('uploadOrders', 'Upload orders')}
        </DialogTitle>

        <IconButton
          onClick={handleDialogClose}
          sx={{
            position: 'absolute',
            right: '1rem',
            top: '1rem',
          }}
        >
          <CloseIcon />
        </IconButton>
        <OrderListUploadStepper store={store} />
        <form onSubmit={handleSubmit(handleValidateAndSubmit)}>
          <OrderListUploadDialogBody
            isRestow={isForRestow}
            visitType={visitType}
            store={store}
            enableVisitSelectField={enableFieldSelectionFromOrdersView}
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
          />

          <OrderListUploadDialogFooter isRestow={isForRestow} store={store} resetForm={reset} />
        </form>
      </Dialog>
    )
  },
)
